import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  useMediaQuery,
  Skeleton,
  Backdrop,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Search } from '@mui/icons-material';
import theme from '../../../styles/Themecolors';
import { MoneyFormatv3 } from '../../../library/MoneyFormat';
import useStockItemWise from '../Hooks/useStockItemWise';
import { ITEM_WISE_COLUMNS } from '../constants/TableHeaderColumns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DateRangeRsuite } from '../../../components';
import { SelectedItem, StockItemWiseData } from '../types';
import { DynamicItemDropdown } from '../components';
import { useThemes } from '../../../components/Theme/theme';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    align?: 'left' | 'center' | 'right';
    width?: string | number;
  }
}

const totalTextStyle = { fontWeight: 'bold' };

const WIDTHS_BASE: Record<string, string> = {
  date: '120px',
  particular: '220px',
  voucher: '130px',
  qty: '100px',
  costItem: '150px',
  totalCost: '150px',
  closingStock: '150px',
  stockAmount: '180px', // Increased from 150px to accommodate "Closing Stock Amount" text
  remark: '600px',
};
const WIDTHS_WITH_FILTERS: Record<string, string> = {
  ...WIDTHS_BASE,
  date: '255px',
  particular: '290px',
  voucher: '200px',
  qty: '150px',
  costItem: '200px',
  totalCost: '200px',
  closingStock: '200px',
  stockAmount: '230px', // Increased from 200px to accommodate "Closing Stock Amount" text
  remark: '400px',
};

const cellBorderCommon = { borderRight: 'none' };

const filterCellSx = (w: string) => ({
  ...cellBorderCommon,
  borderTop: 'none',
  borderBottom: 'none',
  borderLeft: 'none',
  borderRight: 'none',
  width: w,
  maxWidth: w,
  paddingY: 0.5,
  backgroundColor: '#fff',
  position: 'relative',
  zIndex: 1,
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderTop: 'none',
  },
});

const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#e2e8f0',
      borderWidth: '1px',
      borderStyle: 'solid',
    },
    '&:hover fieldset': {
      borderColor: '#94a3b8',
      borderWidth: '1px',
      borderStyle: 'solid',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      borderStyle: 'solid',
    },
    '&.Mui-focused': {
      '& fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
        borderStyle: 'solid',
      },
    },
    height: 40,
    backgroundColor: '#fff',
    borderRadius: '8px',
    position: 'relative',
    zIndex: 1,
    outline: 'none',
    '&:focus': {
      outline: 'none',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: 14,
    lineHeight: '20px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '12px',
    paddingRight: '12px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#64748b',
    opacity: 1,
    fontSize: 13,
  },
  '& .MuiInputAdornment-root': {
    color: '#64748b',
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: '8px',
  },
  '& .MuiInputAdornment-positionEnd': {
    marginRight: '8px',
  },
};



interface StockItemWiseSummaryProps {
  itemDetails?: SelectedItem | null;
  variant?: 'component' | 'modal';
  includeOpeningBalance?: boolean;
  startTimeStamp?: string | number;
  endTimeStamp?: string | number;
}

function StockItemWiseSummary({ itemDetails, variant = 'component', includeOpeningBalance = true, startTimeStamp, endTimeStamp }: StockItemWiseSummaryProps) {
  const isSmDown = useMediaQuery('(max-width: 900px)');
  const { theme } = useThemes();
  const {
    page,
    rowsPerPage,
    rows,
    isItemOpen,
    isItemLoading,
    isDataLoading,
    options,
    selectedItem,
    totals,
    financialYear,
    expandedRows,
    columnFilters,
    showSearch,
    handleChangePage,
    handleChangeRowsPerPage,
    handleItemSelection,
    getRowColor,
    setSearchTerm,
    dateBetweenFn,
    containsText,
    setExpandedRows,
    setColumnFilters,
    setShowSearch,
    loadMoreItems,
    hasMore,
    loadingMore,
    totalItems,
    hasNextPage,
    totalCount,
    itemCodeOptions,
    itemNameOptions,
    itemOptions,
    handleDropdownOpen,
    handleInputClear,
    handleCodeInputChange,
    handleNameInputChange,
    handleCodeInputBlur,
    handleNameInputBlur,
    inputValueCode,
    inputValueName,
    isEditingCode,
    isEditingName,
  } = useStockItemWise(itemDetails, includeOpeningBalance, startTimeStamp, endTimeStamp);

  const getWidth = (id: string): string => (showSearch ? WIDTHS_WITH_FILTERS[id] ?? '140px' : WIDTHS_BASE[id] ?? '140px');

  const columnDefs = useMemo(
    () =>
      ITEM_WISE_COLUMNS.map((col) => {
        const isDate = col.id === 'date';
        const isRemark = col.id === 'remark';
        const isNumeric = ['qty', 'costItem', 'totalCost', 'closingStock', 'stockAmount'].includes(col.id);

        return {
          id: col.id,
          header: col.label,
          accessorKey: col.id,
          meta: { align: col.align, width: getWidth(col.id) },
          enableColumnFilter: !isRemark,
          filterFn: isDate ? dateBetweenFn : containsText,
          cell: (info: any) => {
            const value = info.getValue();
            if (typeof value === 'number' && col.id !== 'qty' && col.id !== 'closingStock') {
              return `₹${MoneyFormatv3(value)}`;
            }

            if (col.id === 'particular') {
              const getNavigationPath = (particular: string) => {
                if (particular && particular.toUpperCase().includes('POS')) {
                  return '/allPos';
                }
                if (particular.includes('Credit Note')) {
                  return '/allcreditnote';
                }
                if (particular.includes('Debit Note')) {
                  return '/alldebitnote';
                }
                if (particular.includes('Purchase')) {
                  return '/allpurchaseandexpense';
                }
                if (particular.includes('Sales')) {
                  return '/salesInvoiceReport';
                }
                return null;
              };

              const navigationPath = getNavigationPath(value);

              const renderTextWithBrackets = (text: string, color: string, isClickable: boolean = false) => {
                const bracketMatch = text.match(/^(.+?)\s*\((.+?)\)$/);
                if (bracketMatch) {
                  const [, mainText, bracketText] = bracketMatch;
                  if (isClickable) {
                    return (
                      <span>
                        <a
                          className="particular-link"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          href={navigationPath || undefined}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: color,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            textDecorationColor: color,
                            textDecorationThickness: '1px',
                            textUnderlineOffset: '2px',
                          } as React.CSSProperties}
                        >
                          {mainText}
                        </a>
                        <span style={{ color: '#000000' }}> ({bracketText})</span>
                      </span>
                    );
                  } else {
                    return (
                      <span>
                        <span style={{ color }}>{mainText}</span>
                        <span style={{ color: '#000000' }}> ({bracketText})</span>
                      </span>
                    );
                  }
                }
                if (isClickable) {
                  return (
                    <a
                      className="particular-link"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      href={navigationPath || undefined}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: color,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        textDecorationColor: color,
                        textDecorationThickness: '1px',
                        textUnderlineOffset: '2px',
                      } as React.CSSProperties}
                    >
                      {text}
                    </a>
                  );
                }
                return <span style={{ color }}>{text}</span>;
              };

              if (navigationPath) {
                return renderTextWithBrackets(value, getRowColor(info.row), true);
              }

              return renderTextWithBrackets(value, getRowColor(info.row), false);
            }

            if (col.id === 'voucher') {
              const invoiceid = info.row.original.invoiceid;
              const particular = info.row.original.particular;

              if (invoiceid && value) {
                let path = '/viewnewinvoicetax';
                if (particular && particular.toUpperCase().includes('POS')) {
                  path = '/viewpos';
                } else if (particular === 'Purchase') {
                  path = '/viewnewpurchasetax';
                } else if (particular === 'Credit Note') {
                  path = '/viewcreditnote';
                } else if (particular === 'Debit Note') {
                  path = '/viewdebitnote';
                }

                return (
                  <a
                    className="voucher-link"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    href={`${path}/${value}?transaction=${invoiceid}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: '#2196f3',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      textDecorationColor: '#2196f3',
                      textDecorationThickness: '1px',
                      textUnderlineOffset: '2px'
                    }}
                  >
                    {value}
                  </a>
                );
              }
              return (
                <Box component="span" sx={{ color: '#2196f3' }}>
                  {value}
                </Box>
              );
            }

            if (col.id === 'remark') {
              return (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    wordBreak: 'keep-all',
                  }}
                  title={value}
                >
                  {value ?? ''}
                </Box>
              );
            }

            return value ?? '';
          },
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expandedRows, showSearch]
  );

  const table = useReactTable<any>({
    data: rows || [],
    columns: columnDefs as any,
    state: {
      columnFilters: columnFilters as any,
      pagination: { pageIndex: page || 0, pageSize: rowsPerPage || 10 },
    },
    onColumnFiltersChange: (updater) => {
      if (typeof updater === 'function') {
        setColumnFilters(updater(columnFilters) as any);
      } else {
        setColumnFilters(updater as any);
      }
    },
    onPaginationChange: (updater) => {
      // Sync table pagination state with hook state
      if (typeof updater === 'function') {
        const newPagination = updater({ pageIndex: page || 0, pageSize: rowsPerPage || 10 });
        if (newPagination.pageSize !== rowsPerPage) {
          handleChangeRowsPerPage({ target: { value: newPagination.pageSize } } as any);
        }
        if (newPagination.pageIndex !== page) {
          handleChangePage(null, newPagination.pageIndex);
        }
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Remove getPaginationRowModel since we're doing server-side pagination
    filterFns: {
      containsText,
      dateBetweenFn,
    },
    manualPagination: true, // Enable manual pagination since we're doing server-side pagination
  });


  const hasFilteredRows = table.getFilteredRowModel().rows.length > 0;

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
      if (hasMore && !loadingMore) {
        loadMoreItems();
      }
    }
  }, [hasMore, loadingMore, loadMoreItems]);

  const handleListboxScroll = useCallback((event: React.UIEvent<HTMLUListElement>) => {
    const target = event.target as HTMLUListElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
      if (hasMore && !loadingMore) {
        loadMoreItems();
      }
    }
  }, [hasMore, loadingMore, loadMoreItems]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box 
        className={variant === 'modal' ? 'noTableCellBorder' : 'PageHeight noTableCellBorder'}
        sx={variant === 'modal' ? {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        } : {}}
      >
        {/* Loading Backdrop */}
        <Backdrop 
          sx={{ 
            color: '#fff', 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }} 
          open={isDataLoading}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ color: 'white' }}>
              Loading item stock data...
            </Typography>
          </Box>
        </Backdrop>

        {/* HEADER BAR */}
        <Box
          sx={{
            backgroundColor: '#fff',
            p: { xs: '8px 12px', sm: '0px 10px' },
            mb: 0.5,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
          }}
        >
          {/* First Row - Title with Search Icon (only show in component mode) */}
          {variant === 'component' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, pt: 0.5 }}>
              <Typography sx={{ 
                fontWeight: 800, 
                fontSize: isSmDown ? 16 : 18,
                color: '#000000',
                textShadow: 'none',
              }}>
                Item Stock Ledger Report (FIFO Method)
              </Typography>
              <Tooltip title="Search" placement="top">
                <Button
                  sx={{
                    borderWidth: '3px',
                    borderColor: '#911B7C',
                    borderStyle: 'solid',
                    backgroundColor: showSearch ? '#f3724a' : '#fff',
                    color: '#911B7C',
                    borderRadius: '5px',
                    minWidth: '30px',
                    height: '28px',
                    width: '30px',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      borderColor: '#911B7C',
                      backgroundColor: showSearch ? '#f3724a' : '#f8fafc',
                      color: showSearch ? '#911B7C' : '#911B7C',
                      transform: 'translateY(-1px)',
                      boxShadow: 'none',
                    },
                    '&:active': {
                      transform: 'translateY(0px) scale(0.95)',
                    },
                  }}
                  variant="outlined"
                  onClick={() => {
                    setShowSearch(!showSearch);
                  }}
                >
                  <SearchIcon sx={{ fontSize: 22 }} />
                </Button>
              </Tooltip>
            </Box>
          )}

          {/* Second Row - Selection Options and Totals */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: isSmDown ? 'flex-start' : 'center', 
            justifyContent: 'space-between', 
            gap: 1, 
            mb: 0.5, 
            flexWrap: 'wrap',
            flexDirection: isSmDown ? 'column' : 'row',
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: isSmDown ? 'flex-start' : 'center', 
              gap: isSmDown ? 1 : 1, 
              flexWrap: 'wrap',
              flexDirection: isSmDown ? 'column' : 'row',
              width: isSmDown ? '100%' : 'auto',
            }}>
              {/* Only show item selection in page mode, not in modal mode */}
              {variant === 'component' && (
                <>
                  <Typography sx={{ 
                    fontWeight: 800, 
                    fontSize: 16, 
                    color: '#374151', 
                    whiteSpace: 'nowrap',
                    textShadow: 'none',
                  }}>
                    Select Item:
                  </Typography>

                  {/* Item Code Dropdown */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                   
                    <Box
                      sx={{
                        width: isSmDown ? '100%' : 200,
                        minWidth: isSmDown ? '100%' : 200,
                        '& .MuiAutocomplete-root': {
                          backgroundColor: '#fff',
                          borderRadius: '12px',
                          boxShadow: 'none',
                          border: '2px solid #e2e8f0',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            boxShadow: 'none',
                            borderColor: '#cbd5e1',
                          },
                          '&.Mui-focused': {
                            boxShadow: 'none',
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    >
                      <DynamicItemDropdown
                        id="item-code-picker"
                        label="Item code"
                        value={isEditingCode ? null : (itemOptions.find((opt) => opt.value === selectedItem?.itemid) || null)}
                        inputValue={inputValueCode}
                        options={itemOptions}
                        loading={isItemLoading}
                        isEditing={isEditingCode}
                        width={isSmDown ? '100%' : 200}
                        onSelectionChange={(item) => {
                          if (item) {
                            handleItemSelection({
                              itemname: item.label,
                              itemid: item.value,
                              itemcode: item.itemcode,
                              unit: item.unit,
                            });
                          } else {
                            handleItemSelection({});
                          }
                        }}
                        onInputChange={handleCodeInputChange}
                        onFocus={handleDropdownOpen}
                        onBlur={handleCodeInputBlur}
                        onOpen={handleDropdownOpen}
                        onClose={(event, reason) => {
                          if (reason === 'blur' || reason === 'toggleInput') {
                            handleCodeInputBlur();
                          }
                        }}
                        onListboxScroll={handleListboxScroll}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.itemcode || option.label;
                        }}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                      />
                    </Box>
                  </Box>

                  {/* Item Name Dropdown */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                   
                    <Box
                      sx={{
                        width: isSmDown ? '100%' : 320,
                        minWidth: isSmDown ? '100%' : 320,
                        '& .MuiAutocomplete-root': {
                          backgroundColor: '#fff',
                          borderRadius: '12px',
                          boxShadow: 'none',
                          border: '2px solid #e2e8f0',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            boxShadow: 'none',
                            borderColor: '#cbd5e1',
                          },
                          '&.Mui-focused': {
                            boxShadow: 'none',
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    >
                      <DynamicItemDropdown
                        id="item-name-picker"
                        label="Item name"
                        value={isEditingName ? null : (itemOptions.find((opt) => opt.value === selectedItem?.itemid) || null)}
                        inputValue={inputValueName}
                        options={itemOptions}
                        loading={isItemLoading}
                        isEditing={isEditingName}
                        width={isSmDown ? '100%' : 320}
                        onSelectionChange={(item) => {
                          if (item) {
                            handleItemSelection({
                              itemname: item.label,
                              itemid: item.value,
                              itemcode: item.itemcode,
                              unit: item.unit,
                            });
                          } else {
                            handleItemSelection({});
                          }
                        }}
                        onInputChange={handleNameInputChange}
                        onFocus={handleDropdownOpen}
                        onBlur={handleNameInputBlur}
                        onOpen={handleDropdownOpen}
                        onClose={(event, reason) => {
                          if (reason === 'blur' || reason === 'toggleInput') {
                            handleNameInputBlur();
                          }
                        }}
                        onListboxScroll={handleListboxScroll}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.label;
                        }}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                      />
                    </Box>
                  </Box>

                  {/* Unit Display - Dynamic width based on text length */}
                  <Box
                    sx={{
                      minWidth: isSmDown ? '100%' : 'auto',
                      maxWidth: isSmDown ? '100%' : 'auto',
                      width: isSmDown ? '100%' : 'auto',
                      display: 'inline-block',
                      '& .MuiTextField-root': {
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        boxShadow: 'none',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s ease-in-out',
                        width: 'auto',
                        minWidth: 'auto',
                        '&:hover': {
                          boxShadow: 'none',
                          borderColor: '#cbd5e1',
                        },
                        '&.Mui-focused': {
                          boxShadow: 'none',
                          borderColor: 'primary.main',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'transparent',
                            borderWidth: '0px',
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                            borderWidth: '0px',
                          },
                        },
                      },
                    }}
                  >
                    <TextField
                      label="UoM"
                      size="small"
                      value={selectedItem?.unit || ''}
                      disabled
                      InputLabelProps={{
                        sx: {
                          color: '#000',
                          fontWeight: 700,
                          fontSize: 11,
                          '&.Mui-focused': { color: '#000' },
                          '&.MuiInputLabel-shrink': {
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#000',
                          },
                        },
                      }}
                      InputProps={{
                        sx: {
                          '& .MuiInputBase-input': {
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#374151',
                            textAlign: 'center',
                            minWidth: 'auto',
                            width: 'auto',
                            paddingLeft: '4px',
                            paddingRight: '4px',
                            paddingTop: '6px',
                            paddingBottom: '6px',
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#000',
                            fontWeight: 700,
                            opacity: 1,
                            fontSize: 10,
                          },
                        },
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          width: 'auto',
                          minWidth: 'auto',
                          height: '32px',
                        },
                        '& .MuiInputBase-input': {
                          paddingLeft: '4px',
                          paddingRight: '4px',
                          textAlign: 'center',
                        },
                      }}
                    />
                  </Box>
                </>
              )}

              {/* Show clean selected item info in modal mode */}
              {variant === 'modal' && itemDetails && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {/* Selected Item Details Box */}
                  <Box
                    sx={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      boxShadow: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: '#374151',
                      whiteSpace: 'nowrap'
                    }}>
                      Selected Item:
                    </Typography>

                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: '#374151',
                      whiteSpace: 'nowrap'
                    }}>
                      Item Code: {itemDetails.itemcode || 'N/A'}
                    </Typography>

                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: '#374151',
                      whiteSpace: 'nowrap'
                    }}>
                      Item Name: {itemDetails.itemname || 'N/A'}
                    </Typography>

                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: '#374151',
                      whiteSpace: 'nowrap'
                    }}>
                      Unit: {itemDetails.unit || 'N/A'}
                    </Typography>
                  </Box>

                  {/* Search Icon - Outside the box */}
                  <Tooltip 
                    title="Search" 
                    placement="top"
                    PopperProps={{
                      style: { zIndex: 9999 },
                      disablePortal: false,
                    }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          zIndex: 9999,
                          backgroundColor: 'rgba(97, 97, 97, 0.9)',
                          fontSize: '12px',
                        },
                      },
                    }}
                  >
                    <Button
                      sx={{
                        borderWidth: '3px',
                        borderColor: '#911B7C',
                        borderStyle: 'solid',
                        backgroundColor: showSearch ? '#f3724a' : '#fff',
                        color: '#911B7C',
                        borderRadius: '5px',
                        minWidth: '30px',
                        height: '28px',
                        width: '30px',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          borderColor: '#911B7C',
                          backgroundColor: showSearch ? '#f3724a' : '#f8fafc',
                          color: showSearch ? '#911B7C' : '#911B7C',
                          transform: 'translateY(-1px)',
                          boxShadow: 'none',
                        },
                        '&:active': {
                          transform: 'translateY(0px) scale(0.95)',
                        },
                      }}
                      variant="outlined"
                      onClick={() => {
                        setShowSearch(!showSearch);
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 22 }} />
                    </Button>
                  </Tooltip>
                </Box>
              )}

            </Box>

            {/* Totals Section */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isSmDown ? 1 : 2, 
              flexWrap: 'wrap',
              width: isSmDown ? '100%' : 'auto',
              justifyContent: isSmDown ? 'center' : 'flex-end',
            }}>
              <Box
                sx={{
                  backgroundColor: 'transparent',
                  // border: '1px solid #e2e8f0',
                  // borderRadius: '12px',
                  px: 2,
                  py: 1,
                }}
              >
                <Typography sx={{ 
                  ...totalTextStyle, 
                  color: '#000000',
                  fontSize: 16,
                  fontWeight: 700,
                }}>
                  {isDataLoading ? <Skeleton width={120} /> : `Opening: ₹${MoneyFormatv3(totals?.totalOp || 0)}`}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'transparent',
                  // border: '1px solid #e2e8f0',
                  // borderRadius: '12px',
                  px: 2,
                  py: 1,
                }}
              >
                <Typography sx={{ 
                  ...totalTextStyle, 
                  color: '#000000',
                  fontSize: 16,
                  fontWeight: 700,
                }}>
                  {isDataLoading ? <Skeleton width={150} /> : `Purchase: ₹${MoneyFormatv3(totals?.totalPurchase || 0)}`}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'transparent',
                  // border: '1px solid #e2e8f0',
                  // borderRadius: '12px',
                  px: 2,
                  py: 1,
                }}
              >
                <Typography sx={{ 
                  ...totalTextStyle, 
                  color: '#000000',
                  fontSize: 16,
                  fontWeight: 700,
                }}>
                  {isDataLoading ? <Skeleton width={120} /> : `Closing: ₹${MoneyFormatv3(totals?.totalClosingStock || 0)}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Paper sx={{
          width: '100%',
          overflow: 'hidden', // Keep hidden for table container
          display: 'flex',
          flexDirection: 'column',
          height: variant === 'modal' ? '100%' : 'auto',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          flex: variant === 'modal' ? 1 : 'none',
          position: 'relative', // Ensure proper positioning context
          boxShadow: 'none',
        }}>
          <TableContainer
            className="custom-scrollbar"
            sx={{
              height: variant === 'modal'
                ? '100%'
                : {
                  sm: 'calc( 100vh - 230px )',
                  xs: 'calc( 100vh - 180px )',
                },
              flex: 1,
              overflowX: 'auto',
              overflowY: 'auto',
              minHeight: 0, // Important for flex scrolling
              // Custom scrollbar styling with purple theme color
              '&::-webkit-scrollbar': {
                width: '11px',
                height: '11px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: (theme?.palette as any)?.bodyGray?.main || '#f1f1f1',
                borderRadius: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme?.palette?.primary?.main || '#911B7C',
                borderRadius: '5px',
                border: '3px solid',
                borderColor: (theme?.palette as any)?.bodyGray?.main || '#f1f1f1',
                '&:hover': {
                  backgroundColor: theme?.palette?.primary?.main || '#911B7C',
                  border: 'none',
                },
              },
              // Firefox scrollbar styling
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme?.palette?.primary?.main || '#911B7C'} ${(theme?.palette as any)?.bodyGray?.main || '#f1f1f1'}`,
            }}
            onScroll={handleScroll}
          >
            <Table
              stickyHeader
              aria-label="stock-table"
              size="small"
              sx={{
                minWidth: 1200,
                tableLayout: 'auto',
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& th, & td': {
                  borderRight: 'none',
                  whiteSpace: 'nowrap',
                },
                '& th:last-of-type, & td:last-of-type': { borderRight: 'none' },
                '& thead': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  backgroundColor: '#fff',
                },
                '& tbody': {
                  zIndex: 1,
                },
                '& thead tr:first-of-type': {
                  '& th': {
                    borderBottom: 'none !important',
                  },
                },
              }}
            >
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <React.Fragment key={headerGroup.id}>
                    {/* Header titles */}
                    <TableRow
                      sx={{
                        '.MuiTableCell-root': {
                          backgroundColor: 'background.tablebg !important',
                          fontSize: '14px',
                          fontWeight: 600,
                          padding: '4px 8px',
                          borderBottom: 'none !important',
                          borderLeft: 'none',
                          borderRight: 'none',
                          position: 'relative',
                          zIndex: 3,
                        },
                      }}
                    >
                      {headerGroup.headers.map((header) => (
                        <TableCell
                          key={header.id}
                          align={header.column.columnDef.meta?.align || 'left'}
                          sx={{
                            width: header.column.columnDef.meta?.width,
                            maxWidth: header.column.columnDef.meta?.width,
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Filter row */}
                    {showSearch && (
                      <TableRow
                        sx={{
                          '.MuiTableCell-root': {
                            backgroundColor: '#fff',
                            padding: '8px 6px',
                            borderTop: 'none',
                            borderBottom: 'none',
                            borderLeft: 'none',
                            borderRight: 'none',
                            position: 'relative',
                            zIndex: 1,
                          },
                          display: showSearch ? 'table-row' : 'none',
                        }}
                      >
                        {headerGroup.headers.map((header) => {
                          const id = header.column.id;
                          const width = header.column.columnDef.meta?.width;
                          const filterVal = header.column.getFilterValue?.();
                          if (id === 'date') {
                            const filterValue = filterVal as { from?: any; to?: any } | undefined;
                            const range = {
                              from: filterValue?.from ? filterValue.from : null,
                              to: filterValue?.to ? filterValue.to : null,
                            };
                            return (
                              <TableCell key={header.id} align={header.column.columnDef.meta?.align || 'left'} sx={filterCellSx(width as string)}>
                                <DateRangeRsuite
                                  value={[range.from, range.to]}
                                  onChange={({ from, to }) => {
                                    header.column.setFilterValue({ from, to });
                                  }}
                                  shouldDisableDate={(date) => {
                                    const startDate = financialYear?.startdate;
                                    const endDate = financialYear?.enddate;
                                    if (!startDate || !endDate) return false;
                                    const dateTime = date.getTime() / 1000;
                                    const startTime = new Date(startDate).getTime() / 1000;
                                    const endTime = new Date(endDate).getTime() / 1000;
                                    return dateTime < startTime || dateTime > endTime;
                                  }}
                                />
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={header.id} align={header.column.columnDef.meta?.align || 'left'} sx={filterCellSx(width as string)}>
                              {header.column.getCanFilter() ? (
                                <TextField
                                  size="small"
                                  placeholder="Search"
                                  value={String(filterVal ?? '')}
                                  onChange={(e) => header.column.setFilterValue(e.target.value)}
                                  fullWidth
                                  sx={filterInputSx}
                                  variant="outlined"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                      </InputAdornment>
                                    ),
                                    endAdornment: filterVal ? (
                                      <InputAdornment position="end">
                                        <IconButton aria-label="clear" size="small" onClick={() => header.column.setFilterValue('')}>
                                          <CloseRoundedIcon fontSize="small" />
                                        </IconButton>
                                      </InputAdornment>
                                    ) : null,
                                  }}
                                />
                              ) : null}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableHead>

              <TableBody>
                {isDataLoading ? (
                  Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={index}>
                      {table.getAllColumns().map((column) => (
                        <TableCell key={column.id} align={column.columnDef.meta?.align || 'left'}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : !hasFilteredRows ? (
                  <TableRow>
                    <TableCell colSpan={table.getAllColumns().length} sx={{ py: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 16, color: '#64748b', fontWeight: 600 }}>No Data Found</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: '#f8fafc' },
                        '.MuiTableCell-root': {
                          padding: '6px 8px',
                          color: '#000000',
                          borderBottom: 'none',
                          borderRight: 'none'
                        },
                        '.MuiTableCell-root:last-child': {
                          borderRight: 'none'
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          align={cell.column.columnDef.meta?.align || 'left'}
                          sx={{
                            fontSize: 13,
                            color: cell.column.id === 'particular' ? getRowColor(row as any) : '#000000',
                            minWidth: cell.column.columnDef.meta?.width,
                            whiteSpace: cell.column.id === 'remark' ? 'normal' : 'nowrap',
                            borderBottom: 'none',
                            borderRight: 'none',
                            '&:last-child': {
                              borderRight: 'none'
                            }
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
            component="div"
            count={totalCount || 0}
            rowsPerPage={rowsPerPage || 10}
            page={page || 0}
            onPageChange={(_, newPage) => {
              handleChangePage(_, newPage);
            }}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              handleChangeRowsPerPage({ target: { value: newSize } } as any);
            }}
            sx={{
              overflow: 'visible', // Allow dropdown to overflow
              position: 'relative',
              zIndex: variant === 'modal' ? 10000 : 'auto', // Higher z-index in modal
              '& .MuiTablePagination-selectLabel': {
                overflow: 'visible',
              },
              '& .MuiTablePagination-displayedRows': {
                overflow: 'visible',
              },
              '& .MuiSelect-select': {
                overflow: 'visible',
              },
              '& .MuiSelect-root': {
                zIndex: variant === 'modal' ? 10000 : 'auto',
              },
              '& .MuiMenu-paper': {
                zIndex: variant === 'modal' ? 10001 : 'auto !important', // Ensure menu appears above modal
              },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: '300px', // Limit dropdown height
                    zIndex: variant === 'modal' ? 10001 : 'auto !important',
                  },
                },
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                // Use portal to render outside modal - this is crucial
                disablePortal: false,
                // Ensure menu can receive pointer events
                style: {
                  zIndex: variant === 'modal' ? 10001 : 'auto',
                },
              },
            }}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}

export default StockItemWiseSummary;
